import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageShell from "../../components/layout/PageShell";
import { OrderTicket } from "../../components/OrderTicket";
import { TextField } from "../../components/ui/Field";
import { FormulaSection } from "./components/FormulaSection";
import { EventDetailsSection } from "./components/EventDetailsSection";
import { AddressSection } from "./components/AddressSection";
import { ContactSection } from "./components/ContactSection";
import { STEPS, useOfferteForm } from "./hooks/useOfferteForm";
import { useFormulas } from "../../hooks/useFormulas";
import { useFormConfig } from "../../hooks/useFormConfig";
import { FormData, Formula } from "./types";

const formatDate = (iso: string) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("nl-BE", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "";

const ticketMeta = (formData: FormData, formula?: Formula) => {
  const meta: { label: string; value: string }[] = [];
  if (formula) meta.push({ label: "Formule", value: formula.name });
  if (formData.datum) meta.push({ label: "Datum", value: formatDate(formData.datum) });
  if (formData.startTijd && formData.eindTijd) meta.push({ label: "Uren", value: `${formData.startTijd} - ${formData.eindTijd}` });
  // Vegetarians are counted among the adults
  if (formData.volwassenen > 0) {
    meta.push({
      label: "Volwassenen",
      value: formData.vegetarisch ? `${formData.volwassenen} (waarvan ${formData.vegetarisch} veggie)` : String(formData.volwassenen),
    });
  }
  if (formData.kinderen > 0) meta.push({ label: "Kinderen", value: String(formData.kinderen) });
  if (formData.eventGemeente) meta.push({ label: "Waar", value: formData.eventGemeente });
  return meta;
};

const TICKET_NOTE = "Je prijs staat op de offerte die je binnen 48 uur per mail krijgt. Daar teken je ze ook online.";

function Offerte() {
  const [searchParams] = useSearchParams();
  const { formulas, loading: formulasLoading, error: formulasError } = useFormulas();
  const config = useFormConfig();
  const form = useOfferteForm(formulas, searchParams.get("formule"), config.minimumPersonen);
  const { formData, step, validationErrors } = form;
  const [ticketOpen, setTicketOpen] = useState(false);

  const formula = formulas.find((item) => item.slug === formData.formula);
  const isLast = step === STEPS.length - 1;

  // Each new step starts at the top of the form, without jumping when it's already in view
  const stepsRef = useRef<HTMLOListElement>(null);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const top = stepsRef.current?.getBoundingClientRect().top ?? 0;
    if (top < 0) stepsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  if (form.submitted) {
    return <Submitted data={form.submitted} formulas={formulas} onStartOver={form.startOver} />;
  }

  return (
    <PageShell>
      <section className="relative overflow-hidden bg-bl-black text-white">
        <div className="plus-pattern absolute inset-0 opacity-[0.04]" aria-hidden="true" />
        <div className="page-container relative py-10 md:py-14">
          <p className="hero-rise eyebrow">Offerte aanvragen</p>
          <h1 className="hero-rise mt-4 font-display text-4xl font-black tracking-tight md:text-5xl" style={{ "--rise-delay": "80ms" } as React.CSSProperties}>
            Stel je foodtruck-feest samen
          </h1>
          <p className="hero-rise mt-3 max-w-2xl text-white/70" style={{ "--rise-delay": "160ms" } as React.CSSProperties}>
            Vijf korte stappen, en binnen 48 uur heb je je offerte in je mailbox.
          </p>
        </div>
      </section>

      <section className="bg-bl-cream pb-28 pt-8 lg:pb-20">
        <div className="page-container grid grid-cols-[minmax(0,1fr)] items-start gap-10 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div>
            {/* Step bar: a real sequence, so the numbers carry meaning */}
            <ol ref={stepsRef} className="flex scroll-mt-4 gap-1.5" aria-label="Stappen">
              {STEPS.map((item, index) => {
                const done = index < step;
                const current = index === step;
                return (
                  <li key={item.key} className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => form.goToStep(index)}
                      disabled={!done}
                      aria-current={current ? "step" : undefined}
                      aria-label={`Stap ${index + 1}: ${item.label}`}
                      className="group w-full text-left disabled:cursor-default"
                    >
                      <span className={`block h-1.5 rounded-full transition-colors duration-500 ${done || current ? "bg-bl-brown" : "bg-bl-black/10"}`} />
                      {/* Labels only fit side by side from tablet width */}
                      <span className={`mt-2 hidden truncate text-xs font-bold uppercase tracking-wider sm:block ${current ? "text-bl-black" : done ? "text-bl-brown group-hover:underline" : "text-bl-black/40"}`}>
                        {index + 1}. {item.label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <p className="mb-6 mt-2 text-xs font-bold uppercase tracking-wider sm:hidden">
              Stap {step + 1} van {STEPS.length} <span className="text-bl-brown">· {STEPS[step].label}</span>
            </p>
            <div className="mb-6 hidden sm:block" />

            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                if (isLast) form.submit();
                else form.goNext();
              }}
              className="rounded-lg bg-white p-5 shadow-sm ring-1 ring-bl-black/5 sm:p-8"
            >
              <div key={step} className="step-in">
                {step === 0 && (
                  <FormulaSection
                    formData={formData}
                    formulas={formulas}
                    loading={formulasLoading}
                    error={formulasError}
                    updateFormData={form.updateFormData}
                    validationErrors={validationErrors}
                  />
                )}
                {step === 1 && (
                  <EventDetailsSection
                    formData={formData}
                    updateFormData={form.updateFormData}
                    validationErrors={validationErrors}
                    minimumPersons={config.minimumPersonen}
                  />
                )}
                {step === 2 && (
                  <fieldset className="space-y-4">
                    <legend className="font-display text-lg font-bold">Waar mogen we de foodtruck zetten?</legend>
                    <p className="text-sm text-bl-black/60">
                      We hebben een vlakke, verharde plaats van ongeveer 6 op 3 meter nodig en stroom op twee aparte circuits.
                    </p>
                    <TextField
                      label="Naam van de locatie"
                      name="eventLocatieNaam"
                      placeholder="Thuis, OC De Kern, Zaal 't Hof…"
                      value={formData.eventLocatieNaam}
                      onValueChange={(value) => form.handleEventAddressChange("eventLocatieNaam", value)}
                      error={validationErrors.eventLocatieNaam}
                    />
                    <AddressSection type="event" formData={formData} onAddressChange={form.handleEventAddressChange} validationErrors={validationErrors} />
                  </fieldset>
                )}
                {step === 3 && (
                  <ContactSection
                    formData={formData}
                    onEventFieldChange={form.handleEventAddressChange}
                    updateFormData={form.updateFormData}
                    onSameAsEventChange={form.handleSameAsEventChange}
                    validationErrors={validationErrors}
                  />
                )}
                {step === 4 && <Overview formData={formData} formula={formula} onEdit={form.goToStep} onNotesChange={(value) => form.updateFormData("opmerkingen", value)} />}
              </div>

              {form.submitError && (
                <p className="mt-6 rounded bg-bl-red/10 px-4 py-3 text-sm text-bl-red" role="alert">
                  {form.submitError}
                </p>
              )}

              <div className="mt-8 flex items-center justify-between gap-3 border-t border-bl-black/10 pt-6">
                {step > 0 ? (
                  <button type="button" onClick={form.goBack} className="btn-ghost">
                    ← Terug
                  </button>
                ) : (
                  <span />
                )}
                <button type="submit" className={isLast ? "btn-red" : "btn-brown"} disabled={form.isSubmitting}>
                  {isLast ? (form.isSubmitting ? "Versturen…" : "Verstuur mijn aanvraag") : `Verder naar ${STEPS[step + 1].label.toLowerCase()}`}
                </button>
              </div>
            </form>
          </div>

          {/* Desktop: the ticket rides along */}
          <aside className="hidden lg:sticky lg:top-6 lg:block">
            <OrderTicket title="Jouw aanvraag" meta={ticketMeta(formData, formula)} note={TICKET_NOTE} />
          </aside>
        </div>
      </section>

      {/* Mobile: the total in a bar, the full ticket on tap */}
      <MobileTicketBar step={step} open={ticketOpen} onToggle={() => setTicketOpen((open) => !open)}>
        <OrderTicket title="Jouw aanvraag" meta={ticketMeta(formData, formula)} note={TICKET_NOTE} />
      </MobileTicketBar>
    </PageShell>
  );
}

function MobileTicketBar({ step, open, onToggle, children }: { step: number; open: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 lg:hidden">
      {open && (
        <div className="step-in max-h-[70vh] overflow-y-auto bg-bl-black/40 px-4 pb-4 pt-10 backdrop-blur-sm" onClick={onToggle}>
          <div onClick={(e) => e.stopPropagation()}>{children}</div>
        </div>
      )}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between bg-bl-black px-5 py-4 text-white"
      >
        <span className="text-sm">
          Jouw aanvraag <span className="text-white/50">· stap {step + 1} van {STEPS.length}</span>
        </span>
        <span className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-wider text-bl-gold">{open ? "Sluit" : "Bekijk bon"}</span>
        </span>
      </button>
    </div>
  );
}

function Overview({ formData, formula, onEdit, onNotesChange }: { formData: FormData; formula?: Formula; onEdit: (step: number) => void; onNotesChange: (value: string) => void }) {
  const rows: { step: number; label: string; value: string }[] = [
    { step: 0, label: "Formule", value: formula?.name ?? "" },
    { step: 1, label: "Volwassenen", value: `${formData.volwassenen}${formData.vegetarisch ? ` (waarvan ${formData.vegetarisch} veggie)` : ""}` },
    { step: 1, label: "Kinderen", value: String(formData.kinderen || 0) },
    { step: 1, label: "Wanneer", value: `${formatDate(formData.datum)}, ${formData.startTijd} - ${formData.eindTijd}` },
    { step: 1, label: "Andere foodtrucks", value: formData.andereFoodtrucks ? "Ja" : "Nee" },
    { step: 2, label: "Locatie", value: `${formData.eventLocatieNaam}, ${formData.eventStraat} ${formData.eventHuisnummer}, ${formData.eventPostcode} ${formData.eventGemeente}` },
    { step: 3, label: "Contact", value: `${formData.eventVoornaam} ${formData.eventNaam} · ${formData.eventEmail} · ${formData.eventPhoneNumber}` },
    {
      step: 3,
      label: "Facturatie",
      value: [formData.bedrijfsnaam, formData.sameAsEvent ? "Zelfde als event" : `${formData.factuurStraat} ${formData.factuurHuisnummer}, ${formData.factuurPostcode} ${formData.factuurGemeente}`, formData.btwnummer]
        .filter(Boolean)
        .join(" · "),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-lg font-bold">Klopt alles?</h2>
        <p className="mt-1 text-sm text-bl-black/60">Na het versturen krijg je een bevestiging per mail en binnen 48 uur je offerte.</p>
      </div>
      <dl className="divide-y divide-bl-black/10 rounded border border-bl-black/10">
        {rows.map((row) => (
          <div key={row.label} className="grid gap-1 px-4 py-3 text-sm sm:grid-cols-[9rem_1fr_auto] sm:items-center sm:gap-4">
            <dt className="text-bl-black/55">{row.label}</dt>
            <dd className="font-medium">{row.value}</dd>
            <dd>
              <button type="button" onClick={() => onEdit(row.step)} className="text-xs font-bold uppercase tracking-wider text-bl-brown hover:underline">
                Wijzig
              </button>
            </dd>
          </div>
        ))}
      </dl>
      <div>
        <label htmlFor="opmerkingen" className="field-label">
          Nog iets dat we moeten weten? <span className="font-normal text-bl-black/45">(optioneel)</span>
        </label>
        <textarea
          id="opmerkingen"
          name="opmerkingen"
          rows={4}
          value={formData.opmerkingen}
          onChange={(e) => onNotesChange(e.target.value)}
          className="field"
          placeholder="Allergieën, een verrassingsfeest, moeilijke toegang…"
        />
      </div>
    </div>
  );
}

function Submitted({ data, formulas, onStartOver }: { data: FormData; formulas: Formula[]; onStartOver: () => void }) {
  const formula = formulas.find((item) => item.slug === data.formula);

  return (
    <PageShell>
      <section className="bg-bl-cream py-14 md:py-20">
        <div className="page-container grid grid-cols-[minmax(0,1fr)] items-center gap-12 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="space-y-5">
            <p className="hero-rise eyebrow">Aanvraag verstuurd</p>
            <h1 className="hero-rise font-display text-4xl font-black tracking-tight md:text-5xl" style={{ "--rise-delay": "80ms" } as React.CSSProperties}>
              Bedankt, {data.eventVoornaam}! Je aanvraag is binnen.
            </h1>
            <div className="hero-rise space-y-3 text-bl-black/75" style={{ "--rise-delay": "160ms" } as React.CSSProperties}>
              <p>
                We sturen een bevestiging naar <strong className="text-bl-black">{data.eventEmail}</strong>. We bekijken je aanvraag en je krijgt binnen 48 uur je offerte, die je online kan ondertekenen.
              </p>
              <p>Geen mail gezien? Kijk even in je spam, of mail ons op <a className="font-medium text-bl-brown underline" href="mailto:ontour@burgerlijk.com">ontour@burgerlijk.com</a>.</p>
            </div>
            <div className="hero-rise flex flex-wrap gap-3 pt-2" style={{ "--rise-delay": "240ms" } as React.CSSProperties}>
              <Link to="/" className="btn-brown">Terug naar On Tour</Link>
              <button type="button" onClick={onStartOver} className="btn-outline">Nog een aanvraag</button>
            </div>
            {/* The animation sits on a cream wrapper so the multiply blend still melts into the page */}
            <div className="hero-rise bg-bl-cream" style={{ "--rise-delay": "320ms" } as React.CSSProperties}>
              <img src="/images/caravan-illustration.jpg" alt="" className="w-full max-w-md mix-blend-multiply" />
            </div>
          </div>
          <OrderTicket
            title="Jouw aanvraag"
            meta={ticketMeta(data, formula)}
            note={TICKET_NOTE}
            stamp={{ text: "Aangevraagd", tone: "red" }}
          />
        </div>
      </section>
    </PageShell>
  );
}

export default Offerte;
